import { useState } from "react";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
  Video,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useUserMeetings } from "@/hooks/use-meeting";
import { useUser } from "@/hooks/useUser";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function Appointments() {
  const navigate = useNavigate();
  const { data: user } = useUser();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // You can adjust this number

  const {
    data: meetings,
    isLoading: isLoadingMeetings,
    error: errorMeetings,
  } = useUserMeetings(user?.menteeUuid);
  console.log("Meetings", meetings);

  // Calculate pagination
  const totalPages = meetings ? Math.ceil(meetings.length / itemsPerPage) : 0;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentMeetings = meetings?.slice(startIndex, endIndex) || [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (isLoadingMeetings) {
    return (
      <div className="max-w-4xl">
        <h1 className="mb-8 text-3xl font-bold">Mentorship Sessions</h1>
        <div className="rounded-xl border bg-white p-6 text-center shadow-sm">
          <p className="text-gray-600">Loading appointments...</p>
        </div>
      </div>
    );
  }

  if (errorMeetings) {
    return (
      <div className="max-w-4xl">
        <h1 className="mb-8 text-3xl font-bold">Mentorship Sessions</h1>
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          <p className="font-medium">{errorMeetings.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="mb-6">
        <h2>Mentoring Sessions</h2>
        <p className="text-muted-foreground">
          Manage your appointments and schedule new sessions with your Vincer
          mentor.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-primary">
            <Calendar className="size-5" />
            Scheduled Sessions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {meetings?.length === 0 && (
            <div className="text-gray-600">You have no upcoming sessions</div>
          )}

          {currentMeetings.map((appointment, index) => (
            <div
              key={appointment.uuid || index}
              className="rounded-lg border border-primary/20 bg-primary/5 p-3"
            >
              <div className="mb-2 flex items-center justify-between">
                <Badge
                  variant="outline"
                  className="border-primary/30 bg-primary/10 text-primary"
                >
                  {appointment.type || ":50 Minute Session"}
                </Badge>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="size-3" />
                    <span>
                      {new Date(
                        appointment.scheduled_time + "Z"
                      ).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "numeric",
                        timeZone: "UTC",
                      })}
                    </span>
                  </div>
                  {appointment.active && (
                    <Button
                      size="sm"
                      onClick={() =>
                        navigate(
                          `/mentee/video-call?sessionId=${appointment.uuid}&authToken=${appointment.mentee_auth_token}`,
                          {
                            state: {
                              mentorName:
                                appointment.mentor?.vincer_base_user?.name,
                            },
                          }
                        )
                      }
                      className="bg-primary px-3 py-1 text-xs text-primary-foreground hover:bg-primary/90"
                    >
                      <Video className="mr-1 size-3" />
                      Join Session
                    </Button>
                  )}
                </div>
              </div>
              <h4>
                {new Date(appointment.scheduled_time + "Z").toLocaleDateString(
                  "en-US",
                  {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    timeZone: "UTC",
                  }
                )}
              </h4>
              <p className="text-muted-foreground">
                {appointment.mentor?.vincer_base_user?.name}
              </p>
            </div>
          ))}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1} to{" "}
                {Math.min(endIndex, meetings?.length || 0)} of{" "}
                {meetings?.length || 0} sessions
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="size-4" />
                  Previous
                </Button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className="w-8 h-8 p-0"
                      >
                        {page}
                      </Button>
                    )
                  )}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}

          <Button className="w-full bg-primary hover:bg-primary/90">
            <Plus className="mr-2 size-4" />
            Schedule New Session
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // return (
  //   <div className="max-w-4xl">
  //     <h1 className="mb-8 text-3xl font-bold">Mentorship Sessions</h1>

  //     <div className="rounded-xl border bg-white shadow-sm">
  //       <div className="divide-y">
  //         {meetings?.map((appointment) => (
  //           <div key={appointment.uuid} className="p-6">
  //             <div className="mb-4 flex items-center justify-between">
  //               <div className="flex items-center gap-4">
  //                 <div className="bg-vincer-50 rounded-lg p-3">
  //                   <User className="text-vincer-600 size-5" />
  //                 </div>
  //                 <div>
  //                   <h3 className="font-medium">
  //                     {appointment.mentor?.vincer_base_user?.name}
  //                   </h3>
  //                   <p className="text-sm text-gray-600">
  //                     {appointment.mentor?.vincer_base_user?.email}
  //                   </p>
  //                 </div>
  //               </div>
  //               <span
  //                 className={`rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(appointment.status || "")}`}
  //               >
  //                 {appointment.status}
  //               </span>
  //             </div>

  //             <div className="flex items-center gap-6 text-sm text-gray-600">
  //               <div className="flex items-center gap-2">
  //                 <Calendar className="size-4" />
  //                 <span>

  //                 </span>
  //               </div>
  //               <div className="flex items-center gap-2">
  //                 <Clock className="size-4" />
  //                 <span>

  //                 </span>
  //               </div>

  //               {/* TODO: Might want to show this only within a certain window of the session */}
  //               {appointment.status === "confirmed" && (
  //                 <div className="ml-auto flex items-center justify-end gap-2">
  //                   <Button asChild>
  //                     <Link
  //                       to={`/mentee/session?sessionId=${appointment.uuid}`}
  //                     >
  //                       Go to Session
  //                     </Link>
  //                   </Button>
  //                 </div>
  //               )}
  //             </div>
  //           </div>
  //         ))}
  //       </div>
  //     </div>
  //   </div>
  // );
}
